import { useMemo, useRef, useState } from 'react';

export interface FileUploadItem {
  id: string;
  file: File;
  status: 'success' | 'error' | 'uploading' | 'pending' | 'incomplete';
}

export interface FileUploadProgress {
  total: number;
  success: number;
  error: number;
  uploading: number;
  pending: number;
  incomplete: number;
}

export interface FileUploadManager {
  files: File[];
  items: FileUploadItem[];
  progress: FileUploadProgress;
  add: (files: File[]) => void;
  remove: (id: FileUploadItem['id']) => void;
  clear: () => void;
  setStatus: (
    id: FileUploadItem['id'],
    status: FileUploadItem['status']
  ) => void;
}

export interface FileUploadManagerHookProps {
  unique?: boolean;
  includeHiddenFiles?: boolean;
  includeNestedFiles?: boolean;
}

export function useFileUploadProgress(
  items: FileUploadItem[]
): FileUploadProgress {
  return useMemo(() => {
    const progress: FileUploadProgress = {
      success: 0,
      error: 0,
      uploading: 0,
      pending: 0,
      incomplete: 0,
      total: items.length
    };
    return items.reduce(
      (progress: FileUploadProgress, item: FileUploadItem) => {
        progress[item.status]++;
        return progress;
      },
      progress
    );
  }, [items]);
}

export function useFileUploadManager(
  props?: FileUploadManagerHookProps
): FileUploadManager {
  const {
    unique = true,
    includeHiddenFiles = false,
    includeNestedFiles = false
  } = props || {};
  const idRef = useRef<number>(0);
  // save items in itemsRef to handle consecutive updates
  // that need access to the latest items list
  // (e.g. consecutive setStatus calls)
  const itemsRef = useRef<FileUploadItem[]>([]);
  const [items, setItemsState] = useState<FileUploadItem[]>([]);
  const progress: FileUploadProgress = useFileUploadProgress(items);

  const files: File[] = useMemo(() => {
    return items.map(({ file }) => file);
  }, [items]);

  // also set to itemsRef
  const setItems = (items: FileUploadItem[]) => {
    itemsRef.current = items;
    setItemsState(items);
  };

  const add = (files: File[]) => {
    files = files.filter((file: File) => {
      const path: string = (file as any).webkitRelativePath || '';
      // exclude hidden files
      if (!includeHiddenFiles && file.name.startsWith('.')) {
        return false;
      }
      // exclude files from subdirectories
      return includeNestedFiles || !path || path.split('/').length <= 2;
    });

    const newItems: FileUploadItem[] = files
      .map((file: File) => {
        const fileUnique: string = `${file.name}-${file.size}`;
        const id: string = !unique
          ? `file-${fileUnique}`
          : `file-${idRef.current++}-${fileUnique}`;
        return { id, file, status: 'pending' } as FileUploadItem;
      })
      .filter((newItem: FileUploadItem) => {
        // filter out existing ids
        const existingItem: FileUploadItem | undefined = itemsRef.current.find(
          (item: FileUploadItem) => item.id === newItem.id
        );
        // if reuploading the same file, update the status back to pending
        if (existingItem) {
          existingItem.status = 'pending';
        }
        return !existingItem;
      });
    setItems(itemsRef.current.concat(newItems));
  };

  const remove = (id: FileUploadItem['id']) => {
    const index: number = itemsRef.current.findIndex(
      (file: FileUploadItem) => file.id === id
    );
    if (index > -1) {
      const newItems: FileUploadItem[] = [...itemsRef.current];
      newItems.splice(index, 1);
      setItems(newItems);
    }
  };

  const clear = () => {
    setItems([]);
    idRef.current = 0;
  };

  const setStatus = (
    id: FileUploadItem['id'],
    status: FileUploadItem['status']
  ) => {
    const index: number = itemsRef.current.findIndex(
      (item: FileUploadItem) => item.id === id
    );
    if (index > -1) {
      const newItems: FileUploadItem[] = [...itemsRef.current];
      newItems[index] = { ...newItems[index], status };
      setItems(newItems);
    }
  };

  const manager: FileUploadManager = useMemo(() => {
    return { items, files, progress, add, remove, clear, setStatus };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, files, progress]);

  return manager;
}
